import { useEffect, useRef, useState } from 'react'
import { isEmailHtmlEmpty, sanitizeEmailHtml } from '@/lib/emailHtml'
import { EditorToolbar, type EditorPlaceholder } from './editortoolbar'
import { cx } from './primitives'

/**
 * The message a customer will read, edited as they will read it.
 *
 * <p>
 * Built on <code>contenteditable</code> and <code>document.execCommand</code> for the same
 * reason the narrative editor is: every browser still implements these commands, and the
 * alternative is an editor library measured in hundreds of kilobytes to produce the same
 * tags. <code>execCommand</code> is deprecated and has no replacement for exactly this, so
 * the trade is written down rather than discovered later.
 * </p>
 *
 * The box is uncontrolled, like every other text field in this application: React does not
 * own the caret. The stored value is written in on mount and again when it changes from
 * outside while nobody is typing - switching template in the Send dialog is that case, and
 * it must not move the cursor of somebody who has carried on writing.
 */
export function EmailEditor({
  id,
  value,
  disabled,
  placeholder,
  minHeight = '14rem',
  placeholders = [],
  onChange,
  onBlur,
}: {
  id?: string
  value: string | null | undefined
  disabled?: boolean
  placeholder?: string
  minHeight?: string
  /** Offered from the toolbar's insert menu. Empty hides the menu rather than showing an empty one. */
  placeholders?: EditorPlaceholder[]
  onChange?: (html: string) => void
  onBlur?: (html: string) => void
}) {
  const host = useRef<HTMLDivElement>(null)
  const [empty, setEmpty] = useState(() => isEmailHtmlEmpty(value))

  // What is on screen, so an echo of our own edit is not written back over the caret.
  const shown = useRef('')

  useEffect(() => {
    const element = host.current
    if (!element) return

    // Sanitised on the way in as well as out: this assigns innerHTML, and what is stored
    // was not necessarily written by this editor.
    const incoming = sanitizeEmailHtml(value ?? '')
    if (incoming === shown.current) return

    // Somebody typing owns the box until they leave it.
    if (document.activeElement === element) return

    element.innerHTML = incoming
    shown.current = incoming
    setEmpty(isEmailHtmlEmpty(incoming))
  }, [value])

  useEffect(() => {
    try {
      // Chrome ends a line with a <div> and Firefox with a <br> unless this is set.
      document.execCommand('defaultParagraphSeparator', false, 'p')
    } catch {
      // An older browser refuses. The sanitiser covers what it produces instead.
    }
  }, [])

  function read() {
    const html = sanitizeEmailHtml(host.current?.innerHTML ?? '')

    // A browser leaves <br> or an empty paragraph behind when the last character goes.
    return isEmailHtmlEmpty(html) ? '' : html
  }

  function handleInput() {
    shown.current = host.current?.innerHTML ?? ''
    setEmpty(isEmailHtmlEmpty(shown.current))
    onChange?.(read())
  }

  function run(command: string, value?: string) {
    const element = host.current
    if (!element || disabled) return

    // Focus first: a command applies to the selection, and clicking a toolbar control takes
    // the selection out of the box unless it is put back.
    element.focus()

    // Colour and size then come back as a styled span rather than a <font> tag, which is
    // what a modern mail client renders most predictably. Asked for with every command
    // rather than once: the switch belongs to the document, not to this box, and the
    // quotation's own editor on the same page throws it the other way.
    try {
      document.execCommand('styleWithCSS', false, 'true')
    } catch {
      // An older browser refuses. The sanitiser covers what it produces instead.
    }

    document.execCommand(command, false, value)
    handleInput()
  }

  function insert(html: string) {
    const element = host.current
    if (!element || disabled) return

    element.focus()
    document.execCommand('insertHTML', false, html)
    handleInput()
  }

  function link() {
    const element = host.current
    if (!element || disabled) return

    element.focus()
    const href = window.prompt('Link address', 'https://')

    // Cancelled, or emptied. Either way there is no link to make.
    if (!href || href.trim().length === 0) return

    document.execCommand('createLink', false, href.trim())
    handleInput()
  }

  return (
    <div
      className={cx(
        'rounded-md border border-line bg-surface',
        'focus-within:border-accent',
        disabled && 'bg-sunken',
      )}
    >
      {!disabled && (
        <EditorToolbar onCommand={run} onLink={link} placeholders={placeholders} onInsert={insert} />
      )}

      <div className="relative">
        <div
          id={id}
          ref={host}
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={() => onBlur?.(read())}
          style={{ minHeight }}
          className={cx(
            'prose-doc w-full overflow-y-auto px-3 py-2 text-sm text-ink focus:outline-none',
            disabled && 'text-muted',
          )}
        />

        {empty && placeholder && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-faint">
            {placeholder}
          </span>
        )}
      </div>
    </div>
  )
}
